<script setup>
import { ref, computed, watchEffect, onMounted, onBeforeUnmount, nextTick } from 'vue';

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  },
  pedido: {
    type: Object,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:open', 'rejeitar', 'refazer', 'cancelar']);

const motivoRejeicao = ref('');
const textareaRef = ref(null);

// Close modal handler
const closeModal = () => {
  emit('update:open', false);
  motivoRejeicao.value = '';
};

// Rejeitar pedido handler
const rejeitarPedido = () => {
  emit('rejeitar', motivoRejeicao.value);
};

// Refazer pedido handler
const refazerPedido = () => {
  if(motivoRejeicao.value.trim() === '') {
    console.log("Motivo de rejeição não pode estar vazio");
    motivoRejeicao.value = "Pedido substituído";
  }
  emit('refazer', motivoRejeicao.value);
};

// Focar na área de texto quando o modal abrir
watchEffect(() => {
  if (props.open && !props.loading) {
    nextTick(() => {
      if (textareaRef.value?.$el) {
        const input = textareaRef.value.$el.querySelector('textarea');
        if (input) {
          setTimeout(() => {
            input.focus();
          }, 50);
        }
      }
    });
  }
});

// Cache the component instance
onMounted(() => {
  // Pre-calculate commonly used values to improve render performance
  const pedidoId = props.pedido?.id || '';
  
  // Update the global open modals tracker
  watchEffect(() => {
    if (props.open) {
      window.pedidoModalOpenCount = (window.pedidoModalOpenCount || 0) + 1;
    } else {
      window.pedidoModalOpenCount = Math.max(0, (window.pedidoModalOpenCount || 0) - 1);
    }
  });
});

onBeforeUnmount(() => {
  // Ensure we decrease the counter when component is unmounted while open
  if (props.open) {
    window.pedidoModalOpenCount = Math.max(0, (window.pedidoModalOpenCount || 0) - 1);
  }
});
</script>

<template>
  <!-- Use teleport para mover o modal para fora da árvore de componentes e evitar modificações -->
  <teleport to="body">
    <div v-if="open" class="modal-backdrop" @click="closeModal"></div>
    
    <div 
      class="modal-container" 
      :class="{ 'modal-open': open }"
      v-if="open"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">
            <v-icon start icon="mdi-close-circle" class="mr-2"></v-icon>
            Rejeitar Pedido #{{ pedido?.id }}
          </h2>
          <button class="modal-close" @click="closeModal">×</button>
        </div>
        
        <div class="modal-body">
          <p class="modal-text">
            Por favor, informe o motivo da rejeição deste pedido:
          </p>
          
          <v-textarea
            ref="textareaRef"
            v-model="motivoRejeicao"
            label="Motivo da rejeição"
            placeholder="Descreva o motivo da rejeição do pedido"
            variant="outlined"
            rows="4"
            counter
            maxlength="255"
            :disabled="loading"
          ></v-textarea>
        </div>
        
        <div class="modal-footer">
          <button
            class="modal-btn cancelar-btn"
            @click="closeModal"
            :disabled="loading"
          >
            Cancelar
          </button>
          
          <div class="modal-actions">
            <button
              class="modal-btn refazer-btn"
              @click="refazerPedido"
              :disabled="loading"
            >
              Refazer
            </button>
            
            <button
              class="modal-btn rejeitar-btn"
              @click="rejeitarPedido"
              :disabled="loading || !motivoRejeicao.trim()"
            >
              <span v-if="loading" class="loader"></span>
              <span v-else>Rejeitar</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<style scoped>

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: transparent;
  z-index: 1000;
}

.modal-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  pointer-events: none;
}

.modal-content {
  width: 100%;
  max-width: 600px;
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.15);
  pointer-events: auto;
  margin: 0 16px;
  transform: translateY(0);
  transition: transform 0.2s ease-out;
  will-change: transform;
}

.modal-open .modal-content {
  transform: translateY(0);
}

.modal-header {
  background-color: #F44336;
  color: white;
  padding: 16px 20px;
  border-radius: 4px 4px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 500;
  margin: 0;
  display: flex;
  align-items: center;
}

.modal-close {
  background: transparent;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 16px;
  transition: background-color 0.2s;
}

.modal-close:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.modal-body {
  padding: 20px 16px 0;
}

.modal-text {
  margin-top: 0;
  margin-bottom: 16px;
  font-size: 1rem;
}

.modal-footer {
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-actions {
  display: flex;
  gap: 8px;
}

.modal-btn {
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  border: none;
  min-width: 64px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cancelar-btn {
  background-color: transparent;
  color: #757575;
}

.cancelar-btn:hover:not(:disabled) {
  background-color: rgba(0, 0, 0, 0.05);
}

.refazer-btn {
  background-color: white;
  color: #3F51B5;
  border: 1px solid currentColor;
}

.refazer-btn:hover:not(:disabled) {
  background-color: rgba(63, 81, 181, 0.05);
}

.rejeitar-btn {
  background-color: #F44336;
  color: white;
}

.rejeitar-btn:hover:not(:disabled) {
  background-color: #D32F2F;
}

/* Loading spinner */
.loader {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  display: inline-block;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 600px) {
  .modal-content {
    max-width: calc(100% - 32px);
  }
  
  .modal-footer {
    flex-direction: column-reverse;
    gap: 16px;
  }
  
  .modal-actions {
    width: 100%;
    justify-content: flex-end;
  }
  
  .cancelar-btn {
    width: 100%;
  }
}
</style>
